import { type StateCreator } from "zustand";

import { companiesService, type TCompanyResponse } from "@/api/companies";
import { tokenStorage } from "@/utils/tokenStorage";
import {
  getSessionExpiredAwareErrorMessage,
  isSessionExpiredHttpError,
  SESSION_EXPIRED_USER_MESSAGE,
} from "@/utils/authUtils";

export type TCompaniesStoreState = {
  companies: TCompanyResponse[] | null;
  isLoading: boolean;
  error: string | null;
};

export type TCompanySection = NonNullable<TCompanyResponse["sections"]>[number];

export type TSectionForCompanyResult = {
  company: TCompanyResponse;
  section: TCompanySection;
} | null;

export type TCompaniesStoreActions = {
  fetchCompanies: (options?: { force?: boolean }) => Promise<void>;
  getCompanies: () => TCompanyResponse[] | null;
  getCompanyById: (id: string) => TCompanyResponse | null;
  getSectionForCompany: (companyId: string, sectionId: string) => TSectionForCompanyResult;
  invalidateCompanies: () => void;
  setCompanies: (companies: TCompanyResponse[]) => void;
};

export type TCompaniesStore = TCompaniesStoreState & TCompaniesStoreActions;

const initialCompaniesStoreState: TCompaniesStoreState = {
  companies: null,
  isLoading: false,
  error: null,
};

/**
 * Check if companies data is empty (null or empty array)
 * Empty arrays should be treated as "no data" to trigger refetch
 */
export const isCompaniesEmpty = (companies: TCompanyResponse[] | null): boolean => {
  return companies === null || companies.length === 0;
};

// Tracks an in-flight companies fetch so concurrent callers await it instead of
// returning stale data. Without this, a `force: true` refresh that coincides with
// another in-flight fetch would short-circuit on `isLoading` and resolve before the
// data is loaded — making route guards miss a just-created company and bounce to /app.
let inflightCompaniesFetch: Promise<void> | null = null;

export const createCompaniesSlice: StateCreator<TCompaniesStore> = (set, get) => ({
  ...initialCompaniesStoreState,

  fetchCompanies: async (options = {}) => {
    const { force = false } = options;

    // Skip if not authenticated - check cookies as single source of truth
    if (!tokenStorage.getAccessToken()) {
      return;
    }

    // If a fetch is already in flight, await it rather than returning stale data.
    // A forced caller then falls through to fetch again so it always sees fresh data
    // (e.g. a route guard verifying a just-created company).
    if (inflightCompaniesFetch) {
      await inflightCompaniesFetch;
      if (!force) {
        return;
      }
    }

    // Skip if companies exist and not forcing refresh (works like profile fetch)
    if (!force && get().companies) {
      return;
    }

    const run = async (): Promise<void> => {
      set({ isLoading: true, error: null });

      try {
        const { data, error } = await companiesService.getCurrentUserCompanies();

        if (error) {
          if (isSessionExpiredHttpError(error)) {
            set({ isLoading: false, error: SESSION_EXPIRED_USER_MESSAGE, companies: null });
            return;
          }

          const errorMessage = await getSessionExpiredAwareErrorMessage(error, "Failed to load companies");
          set({ isLoading: false, error: errorMessage, companies: null });
          return;
        }

        set({ companies: data?.data ?? [], isLoading: false, error: null });
      } catch (err) {
        if (isSessionExpiredHttpError(err)) {
          set({ isLoading: false, error: SESSION_EXPIRED_USER_MESSAGE, companies: null });
          return;
        }

        const errorMessage = err instanceof Error ? err.message : "Failed to load companies";
        set({ isLoading: false, error: errorMessage, companies: null });
      }
    };

    inflightCompaniesFetch = run();
    try {
      await inflightCompaniesFetch;
    } finally {
      inflightCompaniesFetch = null;
    }
  },

  getCompanies: () => {
    const state = get();
    // Trigger fetch if companies is empty (null or empty array)
    if (isCompaniesEmpty(state.companies)) {
      // Fetch in background (don't await)
      void get().fetchCompanies();
    }
    return state.companies;
  },

  getCompanyById: (id: string) => {
    const state = get();
    const companies = state.companies;

    if (isCompaniesEmpty(companies)) {
      // Trigger fetch if no data (null or empty array)
      void get().fetchCompanies();
      return null;
    }

    return companies?.find((c) => c.id === id) ?? null;
  },

  getSectionForCompany: (companyId: string, sectionId: string) => {
    const companies = get().companies;
    if (!companies) return null;
    const company = companies.find((c) => c.id === companyId);
    if (!company?.sections) return null;
    const section = company.sections.find((s) => s.id === sectionId);
    if (!section) return null;
    return { company, section };
  },

  invalidateCompanies: () => {
    set({
      companies: null,
      error: null,
    });
  },

  setCompanies: (companies: TCompanyResponse[]) => {
    set({
      companies,
      error: null,
    });
  },
});
